import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { useI18n, useDispatch, useSelector, useInterval } from '~Hooks';
import { useIsLandscape } from '~Scenes/SelfServiceKiosks/Hooks/useIsLandscape';

const NUMBER_HEIGHT = 230;
const NUMBER_HEIGHT_LANDSCAPE = 149;
// Total timeout time
const TIMEOUT_IN_MS = 30000;
// Time left before timeout warning screen should be shown
const TIMEOUT_WARN = 8;

const Wrapper = styled.div<{ visible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  backdrop-filter: blur(0px);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1001;
  width: 100vw;
  height: 100vh;
  padding: 264px 120px 165px 120px;
  box-sizing: border-box;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transition: opacity 0.5s, visibility 0s 0.5s;

  @media (orientation: landscape) {
    padding: 148px 67px 92px 67px;
  }
`;

const Title = styled.h2`
  margin: 0 0 32px 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 80px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 72px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 68px;
    line-height: 61px;
  }
`;

const Description = styled.p`
  margin: 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-Regular;
  font-size: 48px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 40px;
    line-height: 47px;
  }
`;

const Container = styled.div`
  height: ${NUMBER_HEIGHT * 3}px;
  margin: 60px 0;
  overflow: hidden;
  position: relative;

  @media (orientation: landscape) {
    height: ${NUMBER_HEIGHT_LANDSCAPE * 3}px;
  }
`;

const Numbers = styled.div<{ top: number }>`
  transform: translateY(${({ top }) => top}px);
  transition: transform 0.5s;
  padding-top: ${NUMBER_HEIGHT}px;

  @media (orientation: landscape) {
    padding-top: ${NUMBER_HEIGHT_LANDSCAPE}px;
  }
`;

const Number = styled.div<{ active: boolean }>`
  color: ${({ active }) => (active ? 'rgb(0, 0, 0)' : 'rgba(0, 0, 0, 0.1)')};
  font-family: Eina01-SemiBold;
  font-size: 180px;
  font-weight: 600;
  line-height: ${NUMBER_HEIGHT}px;
  text-align: center;
  height: ${NUMBER_HEIGHT}px;
  transition: color 0.5s;

  @media (orientation: landscape) {
    font-size: 117px;
    line-height: ${NUMBER_HEIGHT_LANDSCAPE}px;
    height: ${NUMBER_HEIGHT_LANDSCAPE}px;
  }
`;

const Fade = styled.div<{ bottom?: boolean }>`
  height: ${Math.round(NUMBER_HEIGHT / 4)}px;
  width: 100%;
  position: absolute;
  top: ${({ bottom }) => (bottom ? 'inherit' : '0px')};
  bottom: ${({ bottom }) => (bottom ? '0px' : 'inherit')};
  left: 0;
  z-index: 1;
  background: linear-gradient(
    ${({ bottom }) => (bottom ? '180deg' : '0deg')},
    transparent,
    #fff 70%
  );

  @media (orientation: landscape) {
    height: ${Math.round(NUMBER_HEIGHT_LANDSCAPE / 4)}px;
  }
`;

interface Props {
  reset: () => void;
  orderId: string | null;
  hasPayment: boolean;
}

const Timeout = ({ reset, orderId, hasPayment }: Props) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const [timeoutVisible, setTimeoutVisible] = useState(false);
  const [active, setActive] = useState(TIMEOUT_WARN);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const activeTimeout = useRef<ReturnType<typeof setTimeout>>();

  const timeOfLastAction = useSelector((state) => state.timeOfLastAction);

  // Every time timeOfLastAction changes,
  // reset timeout counter
  useEffect(() => {
    if (timeOfLastAction) {
      clearTimeout(timeout.current);
      setTimeoutVisible(false);

      activeTimeout.current = setTimeout(() => {
        setActive(TIMEOUT_WARN);
      }, 500);

      timeout.current = setTimeout(() => {
        setTimeoutVisible(true);
      }, TIMEOUT_IN_MS - TIMEOUT_WARN * 1000);
    }

    return () => {
      clearTimeout(timeout.current);
      clearTimeout(activeTimeout.current);
    };
  }, [timeOfLastAction]);

  useEffect(() => {
    if (active < 0) {
      setTimeoutVisible(false);
      timeout.current = setTimeout(() => {
        setActive(TIMEOUT_WARN);
      }, 500);

      dispatch({
        type: 'REMOVE_POS_ORDER',
        id: orderId,
      });

      reset();
    }
  }, [active]);

  useInterval(() => setActive((prev) => --prev), !timeoutVisible || hasPayment ? null : 1000);

  const isLandscape = useIsLandscape();
  const numberHeight = isLandscape ? NUMBER_HEIGHT_LANDSCAPE : NUMBER_HEIGHT;

  return (
    <Wrapper visible={timeoutVisible && !hasPayment}>
      <Title>{i18n('SSK.Timeout.Header')}</Title>
      <Description>{i18n('SSK.Timeout.Subheader')}</Description>
      <Container>
        <Fade />
        <Fade bottom />
        <Numbers top={numberHeight * (active - TIMEOUT_WARN)}>
          {Array.from(Array(TIMEOUT_WARN + 1).keys())
            .reverse()
            .map((idx) => {
              return (
                <Number key={idx} active={active === idx}>
                  {idx}
                </Number>
              );
            })}
        </Numbers>
      </Container>
    </Wrapper>
  );
};

export default Timeout;
